import {
  Image,
  Linking,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  FormComponent,
  FormStructure,
  IFormData,
} from '../../../components/FormHelper/FormHelper.types';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import DatePicker from '../../../components/DatePicker';
import { STYLE_VARS } from '../../../constants/style-vars';
import DiningDropDown from '../../../components/DiningDropDown';
import { VENUE_ITEMS } from '../../../constants/dropdown-items';
import { DropDownItem } from '../../../models/DropDownItem';
import { Button } from '../../../components/Button';
import { TextInput } from '../../../components/TextInput/TextInput';
import { convertToAmPm } from '../../../services/utils/timeConvert';
import { COLORS, FONTS } from '../../../constants';
import DropDown from '../../../components/DropDown/DropDown';
import * as yup from 'yup';
import DiningApi from '../../../services/api/dining.api';
import { setHeaderConfigAxios } from '../../../services/api/axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../../services/redux';
import { IVenue } from '../../../types/dining.types';
import { Loading } from '../../../components';
import RenderHTML from 'react-native-render-html';

type BookingTableFormProps = {
  onSubmitHandler?: (data: IFormData) => void;
};

const BookingTableForm: FC<BookingTableFormProps> = ({ onSubmitHandler }) => {
  const initData = useMemo(() => {
    return {
      date: dayjs(new Date()).format('YYYY-MM-DD'),
      venue: '',
      time: '',
      nAdults: '',
      nChildren: '',
      request: '',
    };
  }, []);
  const [venues, setVenues] = useState<IVenue[]>([]);
  const [isLoadingVenues, setIsLoadingVenues] = useState<boolean>(false);
  const auth = useSelector((redux: RootState) => redux.authReducer);
  const [selectedVenue, setSelectedVenue] = useState<any>();
  const [note, setNote] = useState<string>('');
  const [menuUrl, setMenuUrl] = useState<string>('');
  const { width } = useWindowDimensions();

  const getVenues = useCallback(() => {
    setIsLoadingVenues(true);
    DiningApi.getVenue('dining')
      .then((res) => {
        setVenues(res.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingVenues(false);
      });
  }, []);

  const numberSelect = useCallback((name: string) => {
    const arr: DropDownItem[] = [
      {
        label: 'No ' + name,
        value: 0,
      },
    ];
    for (let i = 1; i <= 100; i++) {
      arr.push({
        label: i + ' ' + name,
        value: i,
      });
    }
    return arr;
  }, []);

  const timeSequence = useMemo(() => {
    const venueArr = venues.filter((venue) => venue.venue[0] === selectedVenue);
    const venue = venueArr[0];
    const result = [];
    const startTime = new Date(`2000-01-01T09:00:00`);
    const endTime = new Date(`2000-01-01T21:00:00`);

    let currentTime = startTime;
    while (currentTime <= endTime) {
      const hours = currentTime.getHours().toString().padStart(2, '0');
      const minutes = currentTime.getMinutes().toString().padStart(2, '0');
      const formattedTime = `${hours}:${minutes}`;
      result.push(formattedTime);
      currentTime.setMinutes(currentTime.getMinutes() + 15);
    }
    return result.map((time) => {
      return {
        label: convertToAmPm(time),
        value: time,
      };
    });
  }, [selectedVenue, venues]);

  const venueItems = useMemo(() => {
    return venues.map((item, index) => {
      return {
        label: item.venue[0],
        value: item.venue[0],
      };
    });
  }, [venues]);

  const bookTableStructure = useMemo(() => {
    const structure: FormStructure = {
      components: [
        {
          name: 'date',
          type: 'datePicker',
          label: 'Select Date',
          zIndex: 105,
        },
        {
          name: 'venue',
          type: 'dropdown',
          label: 'Select Venue',
          items: venueItems,
          zIndex: 104,
          titleDropdown: 'Please Select Venue',
          onChange: (value) => {
            setSelectedVenue(value);
            let venue = venues.find((item) => item.venue[0] === value);

            if (venue) {
              setNote(venue?.note);
              setMenuUrl(venue.menu.url);
            }
          },
        },
        {
          name: 'time',
          type: 'dropdown',
          label: 'Select Time',
          items: timeSequence,
          zIndex: 103,
          titleDropdown: 'Please Select Time',
        },
        {
          name: 'nAdults',
          type: 'dropdown',
          label: 'No. of adults',
          items: numberSelect('adults'),
          zIndex: 102,
          titleDropdown: 'Please Select No. of adults',
        },
        {
          name: 'nChildren',
          type: 'dropdown',
          label: 'No. of children',
          items: numberSelect('children'),
          zIndex: 101,
          titleDropdown: 'Please Select No. of children',
        },
      ],
    };
    return structure;
  }, [numberSelect, timeSequence, venueItems, venues]);

  const renderForm = useCallback((component: FormComponent) => {
    switch (component.type) {
      case 'datePicker':
        return (
          <Field name={component.name}>
            {({ field }: FieldProps) => (
              <View style={{}}>
                <DatePicker
                  date={field.value}
                  onChangeDate={(date) => {
                    field.onChange(field.name)(date);
                  }}
                  haveCalendar={true}
                />
                <ErrorMessage
                  name={field.name}
                  render={(errorMessage: string) => (
                    <Text style={{ color: 'red', marginTop: 5 }}>
                      {errorMessage}
                    </Text>
                  )}
                />
              </View>
            )}
          </Field>
        );
      case 'dropdown':
        return (
          <Field name={component.name}>
            {({ field }: FieldProps) => (
              <View style={{}}>
                <DropDown
                  items={component.items || []}
                  placeholder={'SELECT'}
                  customDownButton={require('../../../assets/down-arrow.png')}
                  customUpButton={require('../../../assets/up-arrow.png')}
                  zIndex={component.zIndex}
                  dropdownStyle={{
                    borderColor: COLORS.DARK_GREY,
                  }}
                  textStyle={{
                    color: COLORS.DARKER_GREY,
                  }}
                  dropDownContainerTextStyle={{
                    color: COLORS.DARKER_GREY,
                  }}
                  buttonStyle={{
                    tintColor: COLORS.DARKER_GREY,
                  }}
                  setValues={(value) => {
                    field.onChange(field.name)(value.value.toString());
                    if (component.onChange) {
                      component.onChange(value.value.toString());
                    }
                  }}
                  title={component.titleDropdown}
                />
                <ErrorMessage
                  name={field.name}
                  render={(errorMessage: string) => (
                    <Text style={{ color: 'red', marginTop: 5 }}>
                      {errorMessage}
                    </Text>
                  )}
                />
              </View>
            )}
          </Field>
        );
      default:
        return <></>;
    }
  }, []);

  useEffect(() => {
    setHeaderConfigAxios(auth.token);
    getVenues();
  }, [getVenues, auth.token]);

  return (
    <View>
      {isLoadingVenues ? (
        <View
          style={{
            marginTop: 30,
            marginBottom: 30,
          }}
        >
          <Loading />
        </View>
      ) : (
        <>
          <View style={[{ flexDirection: 'row' }]}>
            {menuUrl && (
              <TouchableOpacity
                style={{
                  alignSelf: 'flex-start',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: 20,
                }}
                onPress={() => {
                  Linking.openURL(menuUrl);
                }}
              >
                <Image
                  source={require('../../../assets/dining-menus.png')}
                  style={[styles.menuIcon]}
                />
                <Text
                  style={[
                    styles.text,
                    { marginTop: 13, textDecorationLine: 'underline' },
                  ]}
                >
                  View Menu
                </Text>
              </TouchableOpacity>
            )}
          </View>
          <Formik
            initialValues={initData}
            onSubmit={(value) => {
              if (onSubmitHandler) onSubmitHandler(value);
            }}
            validationSchema={yup.object().shape({
              date: yup.string().required('Please Select Date.'),
              venue: yup.string().required('Please Select Venue'),
              time: yup.string().required('Please Select Time'),
              request: yup.string(),
            })}
          >
            {({ handleSubmit, handleChange, values, errors }) => (
              <View
                style={{
                  marginBottom: 20,
                }}
              >
                {bookTableStructure.components.map((component) => (
                  <View
                    key={component.name}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 20,
                      zIndex: component.zIndex,
                    }}
                  >
                    <View style={{ width: '32%' }}>
                      <Text
                        style={{
                          fontFamily: FONTS.IowanOldStyleBTRoman,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          color: STYLE_VARS.textColor,
                        }}
                      >
                        {component.label}
                      </Text>
                    </View>
                    <View style={{ width: '65%' }}>
                      {renderForm(component)}
                    </View>
                  </View>
                ))}
                <View>
                  {note && (
                    <RenderHTML
                      contentWidth={width}
                      source={{ html: note }}
                      tagsStyles={{
                        body: {
                          color: COLORS.RED,
                          fontFamily: FONTS.IowanOldStyleBTRoman,
                          fontSize: 15,
                        },
                        p: {
                          color: COLORS.RED,
                          fontFamily: FONTS.IowanOldStyleBTRoman,
                          fontSize: 15,
                        },
                      }}
                    />
                  )}
                </View>
                <View style={{ marginBottom: 20 }}>
                  <Text
                    style={{
                      color: STYLE_VARS.textColor,
                      textTransform: 'uppercase',
                      fontFamily: FONTS.IowanOldStyleBTRoman,
                      fontSize: 13,
                      fontWeight: '500',
                      marginBottom: 10,
                    }}
                  >
                    Special Requests
                  </Text>
                  <TextInput
                    placeholder={'Message'}
                    onChangeText={handleChange('request')}
                    value={values.request}
                    textAlignVertical="top"
                    multiline
                    placeholderTextColor={STYLE_VARS.fccDarkGrey}
                    style={{
                      height: 100,
                      paddingHorizontal: 10,
                      alignItems: 'flex-start',
                      paddingVertical: 10,
                      borderWidth: 0.5,
                      borderColor: STYLE_VARS.fccDarkGrey,
                      color: STYLE_VARS.fccDarkerGrey,
                      fontSize: 12,
                      letterSpacing: 0.8,
                      fontWeight: '500',
                      fontFamily: FONTS.IowanOldStyleBTRoman,
                    }}
                  />
                  {errors.request && (
                    <Text style={{ color: 'red', marginTop: 5 }}>
                      {errors.request}
                    </Text>
                  )}
                </View>
                <View style={{ marginBottom: 20 }}>
                  <Text
                    style={{
                      color: STYLE_VARS.kccDanger,
                      fontSize: 14,
                      textTransform: 'uppercase',
                      textAlign: 'justify',
                    }}
                  >
                    NOTE: We will confirm your booking within 24 hours.
                  </Text>
                </View>
                <View style={{ alignSelf: 'center', marginBottom: 20 }}>
                  <Button
                    text="BOOK NOW"
                    style={STYLE_VARS.blueButton}
                    onPress={() => {
                      handleSubmit();
                    }}
                  />
                </View>
                <View>
                  <Text
                    style={{
                      fontFamily: FONTS.IowanOldStyleBTRoman,
                      fontSize: 16,
                      color: COLORS.BLACK,
                    }}
                  >
                    <Text
                      style={{
                        fontWeight: 'bold',
                        marginRight: 5,
                      }}
                    >
                      {'Cancellation Policy: '}
                    </Text>
                    {
                      '\nMembers can cancel their bookings within 24 hours of making the reservation. After 24 hours, please call to cancel. For special occasions, kindly provide 7-10 days’ notice for cancellation.'
                    }
                  </Text>
                </View>
              </View>
            )}
          </Formik>
        </>
      )}
    </View>
  );
};

export default BookingTableForm;

const styles = StyleSheet.create({
  text: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 14,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
  },
  menuIcon: {
    width: 20,
    height: 28,
    resizeMode: 'contain',
    tintColor: STYLE_VARS.primaryColor,
    marginRight: 5,
  },
});
